import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tag, Progress, Typography, Button, Alert, Spin, Space, Descriptions, Modal, Select } from 'antd';
import {
    ReloadOutlined, CameraOutlined, PictureOutlined,
    NotificationOutlined, WifiOutlined, ThunderboltOutlined,
    SettingOutlined, ControlOutlined, HddOutlined, AimOutlined,
    AudioOutlined, SoundOutlined, BulbOutlined,
    AlertOutlined, SafetyOutlined, FireOutlined, VideoCameraOutlined,
    GlobalOutlined, ApiOutlined, FileTextOutlined, CodeOutlined,
    PhoneOutlined, LikeOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

// ══════════════════════════════════════════════
//  Mock 数据 — 模拟一台全功能摄像头设备
//  TODO: 联调时替换为套餐接口 + PaaS 设备影子 API
// ══════════════════════════════════════════════

const MOCK_SHADOW = {
    updateTime: '2026-06-22 15:30:12',
    isOnline: true,
    lastReportTime: '2026-06-22 14:20:05',

    // 1003 网络 (主动接口获取，非设备上报)
    network: { netAccessType: 0x02, ssid: 'factory_Home', wifiStrength: 72,
        macAddr: 'AA:BB:CC:11:22:33', ipAddr: '192.168.1.100',
        simSlots: [
            { simSlotId: '0', iccid: '89860115801234567890', signalType: 4, operatorName: '中国移动' },
            { simSlotId: '1', iccid: '89860316901234567891', signalType: 5, operatorName: '中国电信' },
        ],
        workSimSlotId: '0',
    },
    // 1002 SD卡 (主动接口获取，非设备上报)
    storage: { storageStatus: 1, totalSize: 65536, freeSize: 32768, recordFull: 1, recordLoop: 1, duration: 30 },
    // 1040 电池
    battery: { hasBattery: true, powerLevel: 85, powersupply: 1 },
    // 1054 工作模式
    workMode: { curModeId: 1, sleepWaitTime: 30 },

    // ── 设备功能 ──
    // 1011 云端抓图
    cloudSnapshot: { enabled: true, loopDays: 30, gifFlag: 1, picType: 'high' },
    // 1013 事件记录
    eventRecord: { pushFlag: 1, loopDays: 7, interval: 30 },
    // 1005 本地抓图
    localSnapshot: { enabled: true, picType: 'main' },
    // 1051 音频
    audio: { enabled: true, microphoneStatus: 1, autoAGC: 1, audioParam: { channel: 1, depth: 16, encType: 'AAC', sampleRate: 16000 } },
    // 1009 喇叭
    speaker: { enabled: true, volume: 75, voicePlayAbility: 2, timerBuzzer: 0 },
    // 1064 双向视频
    twoWayVideo: { enabled: true, decAbility: [1, 2], resolvAbility: [0x01, 0x1000], screenTime: 60 },
    // 1063 WebRTC
    webRTC: { enabled: true, workType: 1, streamId: 0 },
    // 1053 升级
    ota: { enabled: true, ability: [0x01, 0x04] },
    // 1056 日志
    log: { enabled: true, collectAbility: 1 },

    // ── 灯光 ──
    // 1007 白光灯
    whiteLight: { enabled: true, curModeId: 1, brightness: 80, colorTemperature: 50,
        modeList: [{ modeId: 1, name: '标准', brightness: 80, colorTemperature: 50 }] },
    // 1014 红外灯
    irLight: { enabled: true, ircutType: 0x01, idrDay: 120, idrNight: 80 },
    // 1029 报警灯
    alarmLight: { enabled: true, status: 0 },

    // ── 智能检测 ──
    // 1004 声音检测
    soundDetect: { enabled: true, status: 1, sensitive: 60, interval: 10 },
    // 1037 一键呼叫
    oneKeyCall: { enabled: true, status: 1 },
    // 1034 手势呼叫
    gestureCall: { enabled: true, gestures: { DownEight: 1, DownThump: 1, Hand: 1, Heart: 0, Hush: 0, SignOk: 1, UpEight: 0, UpThump: 0 } },

    // ── 外设 ──
    // 1012 镜头
    camera: { hasCamera: true, camList: [
        { camId: '0', camStatus: 1, streamers: [
            { streamID: '0', width: 1920, height: 1080, frameRate: 15, biteRate: 512 },
            { streamID: '1', width: 640, height: 480, frameRate: 15, biteRate: 1024 }] },
        { camId: '1', camStatus: 0, streamers: [
            { streamID: '0', width: 640, height: 480, frameRate: 15, biteRate: 512 }] },
    ]},
    // 1006 PTZ
    ptz: { hasPtz: true, preSets: [
        { presetId: '1', name: '门口', point: { x: 50, y: 30, z: 2.0 } },
        { presetId: '2', name: '客厅', point: { x: 70, y: 60, z: 1.0 } },
        { presetId: '3', name: '阳台', point: { x: 30, y: 80, z: 1.5 } },
    ], cruises: [
        { cruiseID: '1', openFlag: 1, name: '日常巡逻', cruiseType: 1, autoHome: 1 },
    ]},
};

// ══════════════════════════════════════════════
//  工具函数
// ══════════════════════════════════════════════

const formatMBtoGB = (mb) => (!mb && mb !== 0) ? '--' : (mb / 1024).toFixed(0) + ' GB';

const getWifiStrengthLabel = (s) => {
    if (s >= 60) return { text: '强', color: '#52c41a' };
    if (s >= 30) return { text: '中', color: '#faad14' };
    if (s >= 1) return { text: '弱', color: '#ff4d4f' };
    return { text: '无信号', color: '#d9d9d9' };
};

const OTAAbilityMap = { 0x01: 'TF卡升级', 0x02: '产测工具升级', 0x04: 'OTA 服务升级' };
const VoicePlayMap = { 0: '不支持', 1: '半双工', 2: '全双工' };

// ══════════════════════════════════════════════
//  主组件：DeviceConfigTab
// ══════════════════════════════════════════════

const DeviceConfigTab = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState(null);
    const [networkLoading, setNetworkLoading] = useState(true);
    const [networkError, setNetworkError] = useState(false);
    const [storageLoading, setStorageLoading] = useState(true);
    const [storageError, setStorageError] = useState(false);
    const [jsonModalOpen, setJsonModalOpen] = useState(false);
    const [mockMode, setMockMode] = useState('normal'); // 'normal' | 'networkError' | 'offline'

    const fetchData = () => {
        setLoading(true); setError(false);
        setTimeout(() => {
            const shadow = { ...MOCK_SHADOW, isOnline: mockMode !== 'offline', updateTime: new Date().toLocaleString('zh-CN'), lastReportTime: new Date().toLocaleString('zh-CN') };
            setData(shadow);
            setLoading(false);
        }, 800);
    };

    const fetchNetworkAndStorage = () => {
        setNetworkLoading(true); setStorageLoading(true);
        setNetworkError(false); setStorageError(false);
        if (mockMode === 'offline') { setNetworkLoading(false); setStorageLoading(false); return; }
        setTimeout(() => {
            setNetworkLoading(false);
            if (mockMode === 'networkError') setNetworkError(true);
            setStorageLoading(false);
        }, 1200);
    };

    const handleRefresh = () => { fetchData(); fetchNetworkAndStorage(); };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); fetchNetworkAndStorage(); }, [mockMode]);

    if (loading) return <div style={{ padding: '24px 0' }}><Spin tip="正在加载设备配置数据..."><div style={{ minHeight: 200 }} /></Spin></div>;
    if (error || !data) return <div style={{ padding: '64px 0', textAlign: 'center' }}>
        <div style={{ color: '#ff4d4f', fontSize: 48, marginBottom: 16 }}>!</div>
        <Text type="danger" style={{ display: 'block', marginBottom: 16 }}>数据加载失败，请稍后重试</Text>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>重新加载</Button>
    </div>;

    const showBattery = data.battery.hasBattery;
    const hasPtzOrCamera = data.camera.hasCamera || data.ptz.hasPtz;
    const hasLight = data.whiteLight.enabled || data.irLight.enabled || data.alarmLight.enabled;
    const hasDetect = data.soundDetect.enabled || data.oneKeyCall.enabled || data.gestureCall.enabled;

    return (
        <div style={{ padding: '24px 0' }}>
            <TopBar data={data} onRetry={handleRefresh} onViewJson={() => setJsonModalOpen(true)} />

            {/* ═══ 设备运行状态 ═══ */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Space>
                    <span style={{ fontSize: 16, color: '#1890ff' }}><SettingOutlined /></span>
                    <Text strong style={{ fontSize: 15 }}>设备运行状态</Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 11 }}>mock网络和SD卡</Text>
                <Select value={mockMode} onChange={setMockMode} size="small" style={{ width: 100 }}
                    options={[
                        { label: '正常', value: 'normal' },
                        { label: '网络超时', value: 'networkError' },
                        { label: '设备离线', value: 'offline' },
                    ]} />
            </div>
            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
                <Card4 span={showBattery ? 6 : 8}><NetworkCard data={data.network} loading={networkLoading} error={networkError} isOnline={data.isOnline} /></Card4>
                <Card4 span={showBattery ? 6 : 8}><StorageCard data={data.storage} loading={storageLoading} error={storageError} isOnline={data.isOnline} /></Card4>
                {showBattery && <Card4 span={6}><BatteryCard data={data.battery} /></Card4>}
                <Card4 span={showBattery ? 6 : 8}><WorkModeCard data={data.workMode} /></Card4>
                <Card4 span={6}><CloudSnapshotCard data={data.cloudSnapshot} /></Card4>
                <Card4 span={6}><EventRecordCard data={data.eventRecord} /></Card4>
                <Card4 span={6}><LocalSnapshotCard data={data.localSnapshot} /></Card4>
                <Card4 span={6}><AudioCard data={data.audio} /></Card4>
                <Card4 span={6}><SpeakerCard data={data.speaker} /></Card4>
                <Card4 span={6}><TwoWayVideoCard data={data.twoWayVideo} /></Card4>
                <Card4 span={6}><WebRTCCard data={data.webRTC} /></Card4>
                <Card4 span={6}><OTACard data={data.ota} /></Card4>
                <Card4 span={6}><LogCard data={data.log} /></Card4>
            </Row>

            {/* ═══ 灯光管理 ═══ */}
            {hasLight && (
                <>
                    <SectionTitle icon={<BulbOutlined />} title="灯光管理" />
                    <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
                        {data.whiteLight.enabled && <Card4 span={8}><WhiteLightCard data={data.whiteLight} /></Card4>}
                        {data.irLight.enabled && <Card4 span={8}><IRLightCard data={data.irLight} /></Card4>}
                        {data.alarmLight.enabled && <Card4 span={8}><AlarmLightCard data={data.alarmLight} /></Card4>}
                    </Row>
                </>
            )}

            {/* ═══ 智能检测 ═══ */}
            {hasDetect && (
                <>
                    <SectionTitle icon={<SafetyOutlined />} title="智能检测" />
                    <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
                        {data.soundDetect.enabled && <Card4 span={8}><SoundDetectCard data={data.soundDetect} /></Card4>}
                        {data.oneKeyCall.enabled && <Card4 span={8}><OneKeyCallCard data={data.oneKeyCall} /></Card4>}
                        {data.gestureCall.enabled && <Card4 span={8}><GestureCallCard data={data.gestureCall} /></Card4>}
                    </Row>
                </>
            )}

            {/* ═══ 外设状态 ═══ */}
            {hasPtzOrCamera && (
                <>
                    <SectionTitle icon={<ControlOutlined />} title="外设状态" />
                    <Row gutter={[16, 16]}>
                        {data.camera.hasCamera && <Card4 span={data.ptz.hasPtz ? 12 : 24}><CameraCard data={data.camera} /></Card4>}
                        {data.ptz.hasPtz && <Card4 span={data.camera.hasCamera ? 12 : 24}><PtzCard data={data.ptz} /></Card4>}
                    </Row>
                </>
            )}

            {/* JSON 配置弹窗 */}
            <Modal title="设备配置 JSON" open={jsonModalOpen} onCancel={() => setJsonModalOpen(false)} width={700}
                footer={<Button onClick={() => setJsonModalOpen(false)}>关闭</Button>}>
                <pre style={{ maxHeight: 500, overflow: 'auto', background: '#f5f5f5', padding: 16, borderRadius: 8, fontSize: 12, lineHeight: 1.6 }}>
                    {JSON.stringify(data, null, 2)}
                </pre>
            </Modal>
        </div>
    );
};

// ══════════════════════════════════════════════
//  通用组件
// ══════════════════════════════════════════════

const TopBar = ({ data, onRetry, onViewJson }) => (
    <div style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
            <Col><Text type="secondary" style={{ fontSize: 13 }}>数据更新时间：{data.updateTime}</Text></Col>
            <Col>
                <Space>
                    <Button size="small" icon={<CodeOutlined />} onClick={onViewJson}>查看配置</Button>
                    <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>刷新</Button>
                </Space>
            </Col>
        </Row>
        {data.isOnline === false && <Alert message={`设备当前离线，以下数据为最近一次上报（${data.lastReportTime}）`} type="warning" showIcon style={{ marginTop: 12, borderRadius: 8 }} />}
    </div>
);

const SectionTitle = ({ icon, title }) => (
    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 16, color: '#1890ff' }}>{icon}</span><Text strong style={{ fontSize: 15 }}>{title}</Text></div>
);

const Card4 = ({ children, span }) => (
    <Col span={span}><Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }} bodyStyle={{ padding: '16px 20px' }}>{children}</Card></Col>
);

const DimText = () => <span style={{ color: '#ccc' }}>--</span>;

// ══════════════════════════════════════════════
//  设备运行状态 — 网络 / 存储 / 电池 / 工作模式
// ══════════════════════════════════════════════

const NetworkCard = ({ data, loading: netLoading, error: netErr, isOnline }) => {
    const m = { 0x01: { t: '有线', c: 'blue' }, 0x02: { t: 'WiFi', c: 'green' }, 0x08: { t: '4G', c: 'orange' } };
    const nt = m[data.netAccessType] || { t: '未知', c: 'default' };
    const wl = getWifiStrengthLabel(data.wifiStrength);

    // 设备离线
    if (isOnline === false) {
        return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><WifiOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>网络信息</Text></div><div style={{ padding: '24px 8px', textAlign: 'center' }}><Text type="warning" style={{ fontSize: 13 }}>设备离线，暂无法获取</Text></div></div>;
    }
    // 加载中
    if (netLoading) return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><WifiOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>网络信息</Text></div><Spin size="small" style={{ display: 'block', textAlign: 'center', padding: 16 }} /></div>;
    // 获取失败
    if (netErr) return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><WifiOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>网络信息</Text></div><div style={{ padding: '24px 8px', textAlign: 'center' }}><Text type="danger" style={{ fontSize: 13 }}>获取失败，请稍候重试</Text></div></div>;

    return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><WifiOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>网络信息</Text><Tag color={nt.c} style={{ marginLeft: 'auto', border: 'none' }}>{nt.t}</Tag></div>{data.netAccessType === 0x02 && <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}><Descriptions.Item label="SSID"><Text>{data.ssid || '--'}</Text></Descriptions.Item><Descriptions.Item label="信号强度"><Space><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: wl.color, marginRight: 4 }} /><Text>{data.wifiStrength} ({wl.text})</Text></Space></Descriptions.Item><Descriptions.Item label="MAC 地址"><Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{data.macAddr || '--'}</Text></Descriptions.Item><Descriptions.Item label="IP 地址"><Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{data.ipAddr || '--'}</Text></Descriptions.Item></Descriptions>}{data.netAccessType === 0x08 && data.simSlots && (
        <div>
            {data.simSlots.map((s) => (
                <div key={s.simSlotId} style={{ marginBottom: 8, padding: '8px 10px', background: '#f9f9f9', borderRadius: 6, borderLeft: `3px solid ${data.workSimSlotId === s.simSlotId ? '#52c41a' : '#d9d9d9'}` }}>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: 12 }}>卡槽 {s.simSlotId}{data.workSimSlotId === s.simSlotId ? ' (工作中)' : ''}</Text>
                        <Tag color="blue" style={{ border: 'none', fontSize: 11 }}>{s.signalType === 5 ? '5G' : s.signalType === 4 ? '4G' : `${s.signalType}G`}</Tag>
                    </Row>
                    <Descriptions column={2} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 10 }}>
                        <Descriptions.Item label="SIM 卡号"><Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.iccid}</Text></Descriptions.Item>
                        <Descriptions.Item label="运营商"><Text style={{ fontSize: 11 }}>{s.operatorName || '--'}</Text></Descriptions.Item>
                    </Descriptions>
                </div>
            ))}
            <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
                <Descriptions.Item label="MAC 地址"><Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{data.macAddr || '--'}</Text></Descriptions.Item>
                <Descriptions.Item label="IP 地址"><Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{data.ipAddr || '--'}</Text></Descriptions.Item>
            </Descriptions>
        </div>
    )}{data.netAccessType === 0x01 && <><Text type="secondary" style={{ fontSize: 12 }}>有线以太网接入</Text><Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}><Descriptions.Item label="MAC 地址"><Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{data.macAddr || '--'}</Text></Descriptions.Item><Descriptions.Item label="IP 地址"><Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{data.ipAddr || '--'}</Text></Descriptions.Item></Descriptions></>}</div>;
};

const StorageCard = ({ data, loading: storLoading, error: storErr, isOnline }) => {
    const sm = { 0: { t: '无卡', c: 'default' }, 1: { t: '正常', c: 'green' }, 2: { t: '异常', c: 'orange' } };
    const tf = sm[data.storageStatus] || { t: '未知', c: 'default' };
    const hc = data.storageStatus === 1;
    const up = hc && data.totalSize > 0 ? Math.round(((data.totalSize - data.freeSize) / data.totalSize) * 100) : 0;

    if (isOnline === false) return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><HddOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>SD卡存储</Text></div><div style={{ padding: '24px 8px', textAlign: 'center' }}><Text type="warning" style={{ fontSize: 13 }}>设备离线，暂无法获取</Text></div></div>;
    if (storLoading) return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><HddOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>SD卡存储</Text></div><Spin size="small" style={{ display: 'block', textAlign: 'center', padding: 16 }} /></div>;
    if (storErr) return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><HddOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>SD卡存储</Text></div><div style={{ padding: '24px 8px', textAlign: 'center' }}><Text type="danger" style={{ fontSize: 13 }}>获取失败，请稍候重试</Text></div></div>;

    return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><HddOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>SD卡存储</Text><Tag color={tf.c} style={{ marginLeft: 'auto', border: 'none' }}>{tf.t}</Tag></div>{hc ? <><div style={{ marginBottom: 8 }}><Row justify="space-between" style={{ marginBottom: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>已用 / 总容量</Text><Text style={{ fontSize: 12 }}>{formatMBtoGB(data.totalSize - data.freeSize)} / {formatMBtoGB(data.totalSize)}</Text></Row><Progress percent={up} size="small" strokeColor={up >= 80 ? '#ff4d4f' : '#1890ff'} /></div><Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}><Descriptions.Item label="本地录像"><Tag color={data.recordFull === 1 ? 'blue' : 'default'} style={{ border: 'none' }}>{data.recordFull === 1 ? '全天录像' : '事件录像'}</Tag></Descriptions.Item><Descriptions.Item label="循环覆盖"><Tag color={data.recordLoop === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{data.recordLoop === 1 ? '是' : '否'}</Tag></Descriptions.Item><Descriptions.Item label="录像时长"><Text>{data.duration}s</Text></Descriptions.Item></Descriptions></> : <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}><Descriptions.Item label="总容量"><DimText /></Descriptions.Item><Descriptions.Item label="剩余容量"><DimText /></Descriptions.Item><Descriptions.Item label="录像模式"><DimText /></Descriptions.Item><Descriptions.Item label="循环覆盖"><DimText /></Descriptions.Item></Descriptions>}</div>;
};

const BatteryCard = ({ data }) => {
    const lo = data.powerLevel <= 20;
    const cg = data.powersupply === 1;
    return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><ThunderboltOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>电池信息</Text></div><div style={{ textAlign: 'center', marginBottom: 12 }}><Text style={{ fontSize: 32, fontWeight: 600, color: lo ? '#ff4d4f' : '#52c41a' }}>{data.powerLevel}%</Text></div><div style={{ textAlign: 'center' }}><Tag color={cg ? 'green' : 'default'} style={{ border: 'none' }}>{cg ? '⚡ 充电中' : '未充电'}</Tag></div></div>;
};

const WorkModeCard = ({ data }) => {
    const mm = { 1: { t: '长电', c: 'blue' }, 2: { t: '低功耗', c: 'orange' }, 3: { t: '超低功耗', c: 'red' } };
    const m = mm[data.curModeId] || { t: '未知', c: 'default' };
    return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><ControlOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>工作模式</Text></div><div style={{ textAlign: 'center', marginBottom: 16 }}><Tag color={m.c} style={{ fontSize: 15, padding: '4px 16px', border: 'none' }}>{m.t}</Tag></div><Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}><Descriptions.Item label="休眠等待"><Text>{data.sleepWaitTime}s</Text></Descriptions.Item></Descriptions></div>;
};

// ══════════════════════════════════════════════
//  设备运行状态 — 功能类
// ══════════════════════════════════════════════

const CloudSnapshotCard = ({ data }) => (
    <CompactCard icon={<PictureOutlined style={{ color: '#722ed1' }} />} title="云端抓图" tag={data.enabled ? '已开启' : '未开启'} tagColor={data.enabled ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="存储天数">{data.loopDays} 天</Descriptions.Item>
            <Descriptions.Item label="GIF动图"><Tag color={data.gifFlag === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{data.gifFlag === 1 ? '开启' : '关闭'}</Tag></Descriptions.Item>
            <Descriptions.Item label="抓图质量">{data.picType === 'high' ? '高质量' : '低质量'}</Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const EventRecordCard = ({ data }) => (
    <CompactCard icon={<NotificationOutlined style={{ color: '#722ed1' }} />} title="事件记录" tag={data.pushFlag === 1 ? '已推送' : '未推送'} tagColor={data.pushFlag === 1 ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="存储天数">{data.pushFlag === 1 ? `${data.loopDays} 天` : <DimText />}</Descriptions.Item>
            <Descriptions.Item label="推送间隔">{data.pushFlag === 1 ? `${data.interval}s` : <DimText />}</Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const LocalSnapshotCard = ({ data }) => (
    <CompactCard icon={<PictureOutlined style={{ color: '#722ed1' }} />} title="本地抓图" tag={data.enabled ? '已启用' : '未启用'} tagColor={data.enabled ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="图片类型">{data.picType === 'main' ? '主码流分辨率' : data.picType === 'sub' ? '次码流分辨率' : data.picType}</Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const AudioCard = ({ data }) => (
    <CompactCard icon={<AudioOutlined style={{ color: '#722ed1' }} />} title="音频管理" tag={data.microphoneStatus === 1 ? '已开启' : '已关闭'} tagColor={data.microphoneStatus === 1 ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="麦克风"><Tag color={data.microphoneStatus === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{data.microphoneStatus === 1 ? '开启' : '关闭'}</Tag></Descriptions.Item>
            <Descriptions.Item label="自动增益"><Tag color={data.autoAGC === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{data.autoAGC === 1 ? '开启' : '关闭'}</Tag></Descriptions.Item>
            <Descriptions.Item label="编码格式"><Text style={{ fontSize: 12 }}>{data.audioParam.encType} / {data.audioParam.sampleRate}Hz / {data.audioParam.channel}声道</Text></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const SpeakerCard = ({ data }) => (
    <CompactCard icon={<SoundOutlined style={{ color: '#722ed1' }} />} title="喇叭管理" tag={`音量 ${data.volume}%`} tagColor="blue">
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="对讲能力"><Text>{VoicePlayMap[data.voicePlayAbility] || data.voicePlayAbility}</Text></Descriptions.Item>
            <Descriptions.Item label="定时播报"><Tag color={data.timerBuzzer === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{data.timerBuzzer === 1 ? '开启' : '关闭'}</Tag></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const TwoWayVideoCard = ({ data }) => (
    <CompactCard icon={<VideoCameraOutlined style={{ color: '#722ed1' }} />} title="双向视频" tag={data.enabled ? '已支持' : '不支持'} tagColor={data.enabled ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="解码能力"><Text style={{ fontSize: 12 }}>{data.decAbility.map(v => v === 1 ? 'MJPEG' : v === 2 ? 'H.264' : v === 4 ? 'H.265' : v).join(', ')}</Text></Descriptions.Item>
            <Descriptions.Item label="息屏时间"><Text>{data.screenTime}s</Text></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const WebRTCCard = ({ data }) => (
    <CompactCard icon={<GlobalOutlined style={{ color: '#722ed1' }} />} title="WebRTC" tag={data.enabled ? '已启用' : '未启用'} tagColor={data.enabled ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="工作模式"><Tag color="blue" style={{ border: 'none' }}>{data.workType === 1 ? '云端' : '本地'}</Tag></Descriptions.Item>
            <Descriptions.Item label="码流"><Tag style={{ border: 'none' }}>{data.streamId === 0 ? '主码流' : '次码流'}</Tag></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const OTACard = ({ data }) => {
    const abilities = (data.ability || []).map(a => OTAAbilityMap[a] || `0x${a.toString(16)}`).join('、');
    return (
        <CompactCard icon={<ApiOutlined style={{ color: '#722ed1' }} />} title="升级能力" tag={abilities ? `${data.ability.length} 种方式` : '无'} tagColor="blue">
            <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
                <Descriptions.Item label="支持方式"><Text style={{ fontSize: 12 }}>{abilities || <DimText />}</Text></Descriptions.Item>
            </Descriptions>
        </CompactCard>
    );
};

const LogCard = ({ data }) => (
    <CompactCard icon={<FileTextOutlined style={{ color: '#722ed1' }} />} title="日志管理" tag={data.collectAbility > 0 ? '已支持' : '不支持'} tagColor={data.collectAbility > 0 ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="日志收集"><Tag color={data.collectAbility > 0 ? 'green' : 'default'} style={{ border: 'none' }}>{data.collectAbility > 0 ? '支持' : '不支持'}</Tag></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

// ══════════════════════════════════════════════
//  灯光管理
// ══════════════════════════════════════════════

const WhiteLightCard = ({ data }) => {
    const mode = data.modeList?.find(m => m.modeId === data.curModeId);
    return (
        <CompactCard icon={<BulbOutlined style={{ color: '#faad14' }} />} title="白光灯" tag={mode?.name || '--'} tagColor="orange">
            <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
                <Descriptions.Item label="亮度"><Text>{data.brightness} / 100</Text></Descriptions.Item>
                <Descriptions.Item label="色温"><Text>{data.colorTemperature} / 100</Text></Descriptions.Item>
            </Descriptions>
        </CompactCard>
    );
};

const IRLightCard = ({ data }) => {
    const ircutMap = { 0x01: '全自动', 0x02: '手动+自动', 0x03: '仅手动' };
    return (
        <CompactCard icon={<FireOutlined style={{ color: '#fa541c' }} />} title="红外灯" tag={ircutMap[data.ircutType] || '--'} tagColor="volcano">
            <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
                <Descriptions.Item label="切换方式"><Text>{ircutMap[data.ircutType] || data.ircutType}</Text></Descriptions.Item>
                <Descriptions.Item label="白天阈值"><Text>{data.idrDay}</Text></Descriptions.Item>
                <Descriptions.Item label="夜晚阈值"><Text>{data.idrNight}</Text></Descriptions.Item>
            </Descriptions>
        </CompactCard>
    );
};

const AlarmLightCard = ({ data }) => (
    <CompactCard icon={<AlertOutlined style={{ color: '#ff4d4f' }} />} title="报警灯" tag={data.status === 1 ? '开启' : '关闭'} tagColor={data.status === 1 ? 'red' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="当前状态"><Tag color={data.status === 1 ? 'red' : 'default'} style={{ border: 'none' }}>{data.status === 1 ? '亮' : '灭'}</Tag></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

// ══════════════════════════════════════════════
//  智能检测
// ══════════════════════════════════════════════

const SoundDetectCard = ({ data }) => (
    <CompactCard icon={<AlertOutlined style={{ color: '#eb2f96' }} />} title="声音检测" tag={data.status === 1 ? '已启用' : '已关闭'} tagColor={data.status === 1 ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="灵敏度"><Text>{data.sensitive} dB</Text></Descriptions.Item>
            <Descriptions.Item label="检测间隔"><Text>{data.interval}s</Text></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const OneKeyCallCard = ({ data }) => (
    <CompactCard icon={<PhoneOutlined style={{ color: '#eb2f96' }} />} title="一键呼叫" tag={data.status === 1 ? '已启用' : '已关闭'} tagColor={data.status === 1 ? 'green' : 'default'}>
        <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
            <Descriptions.Item label="功能状态"><Tag color={data.status === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{data.status === 1 ? '开启' : '关闭'}</Tag></Descriptions.Item>
        </Descriptions>
    </CompactCard>
);

const GestureCallCard = ({ data }) => {
    const enabledGestures = Object.entries(data.gestures || {}).filter(([, v]) => v === 1);
    const gestureNames = { DownEight: '下八', DownThump: '下指', Hand: '挥手', Heart: '比心', Hush: '嘘', SignOk: 'OK', UpEight: '上八', UpThump: '上指' };
    return (
        <CompactCard icon={<LikeOutlined style={{ color: '#eb2f96' }} />} title="手势呼叫" tag={`${enabledGestures.length} 种手势`} tagColor="pink">
            <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
                <Descriptions.Item label="已启用手势">
                    {enabledGestures.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {enabledGestures.map(([k]) => <Tag key={k} color="purple" style={{ border: 'none', fontSize: 11 }}>{gestureNames[k] || k}</Tag>)}
                        </div>
                    ) : <DimText />}
                </Descriptions.Item>
            </Descriptions>
        </CompactCard>
    );
};

// ══════════════════════════════════════════════
//  外设 — 镜头 / PTZ
// ══════════════════════════════════════════════

const CameraCard = ({ data }) => (
    <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}><CameraOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>镜头信息</Text></div>{data.camList.map(cam => { const ms = cam.streamers.find(s => s.streamID === '0'); return <div key={cam.camId} style={{ marginBottom: 12, padding: '12px 16px', background: '#f9f9f9', borderRadius: 8, borderLeft: `3px solid ${cam.camStatus === 1 ? '#52c41a' : '#d9d9d9'}` }}><Row justify="space-between" align="middle" style={{ marginBottom: 8 }}><Text strong style={{ fontSize: 13 }}>Cam{cam.camId}</Text><Tag color={cam.camStatus === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{cam.camStatus === 1 ? '开启' : '关闭'}</Tag></Row>{ms && <Descriptions column={2} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 11 }}><Descriptions.Item label="分辨率"><Text style={{ fontSize: 12 }}>{ms.width}×{ms.height}</Text></Descriptions.Item><Descriptions.Item label="帧率"><Text style={{ fontSize: 12 }}>{ms.frameRate}fps</Text></Descriptions.Item>{cam.streamers.length > 1 && <><Descriptions.Item label="主码流"><Text style={{ fontSize: 12 }}>{cam.streamers[0].biteRate}kbps</Text></Descriptions.Item><Descriptions.Item label="次码流"><Text style={{ fontSize: 12 }}>{cam.streamers[1].biteRate}kbps</Text></Descriptions.Item></>}</Descriptions>}</div>; })}</div>
);

const PtzCard = ({ data }) => {
    const ac = data.cruises.find(c => c.openFlag === 1);
    return <div><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}><AimOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 14 }}>PTZ 信息</Text></div><Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>预置位 ({data.preSets.length} 个)</Text>{data.preSets.length > 0 ? <div style={{ marginBottom: 16 }}>{data.preSets.map(p => <div key={p.presetId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', marginBottom: 4, background: '#f9f9f9', borderRadius: 6 }}><Space size={4}><Text style={{ fontSize: 12, color: '#1890ff', fontWeight: 500 }}>#{p.presetId}</Text><Text style={{ fontSize: 13 }}>{p.name}</Text></Space><Text type="secondary" style={{ fontSize: 11 }}>({p.point.x},{p.point.y}) ×{p.point.z}</Text></div>)}</div> : <div style={{ marginBottom: 16 }}><DimText /></div>}<Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>巡航状态</Text>{ac ? <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}><Descriptions.Item label="状态"><Tag color="green" style={{ border: 'none' }}>开启</Tag></Descriptions.Item><Descriptions.Item label="轨迹名称"><Text>{ac.name}</Text></Descriptions.Item><Descriptions.Item label="巡航方式"><Text>{ac.cruiseType === 1 ? '往返' : '单向'}</Text></Descriptions.Item><Descriptions.Item label="自动回归"><Tag color={ac.autoHome === 1 ? 'green' : 'default'} style={{ border: 'none' }}>{ac.autoHome === 1 ? '是' : '否'}</Tag></Descriptions.Item></Descriptions> : <DimText />}</div>;
};

// ══════════════════════════════════════════════
//  CompactCard — 简单卡片通用容器
// ══════════════════════════════════════════════

const CompactCard = ({ icon, title, tag, tagColor, children }) => (
    <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            {icon}<Text strong style={{ fontSize: 14 }}>{title}</Text>
            <Tag color={tagColor} style={{ marginLeft: 'auto', border: 'none' }}>{tag}</Tag>
        </div>
        {children}
    </div>
);

export default DeviceConfigTab;
